import React, { useState } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useOpenClose } from 'lib/hooks/use-boolean-state';
import ManageControls from './manage-controls';
import { getFadeInProps, getSlideLeftProps } from 'lib/helpers/transition';
import { useQueryString } from 'lib/hooks/use-query-string';

const transitions = {
    opacity100: getFadeInProps({ enter: 100, leave: 100 }),
    translate300: getSlideLeftProps({ enter: 300, leave: 300 })
};

export function useToggleSlideOver(queryStringKey) {
    const [id, setId] = React.useState(null);
    const [queryStringId, setQueryStringId] = useQueryString(queryStringKey, null);

    const selectedId = queryStringKey ? queryStringId : id;
    const isCreate = selectedId === null;

    const setSelectedId = React.useCallback(
        (id) => {
            if (queryStringKey) {
                setQueryStringId(id);
                return;
            }
            setId(id);
        },
        [queryStringKey, setQueryStringId]
    );

    const { isOpen, open, close } = useOpenClose({
        initialState: { isOpen: Boolean(selectedId) },
        onClose: () => {
            if (selectedId) setSelectedId(null);
        }
    });

    const openExisting = React.useCallback(
        (id) => {
            setSelectedId(id);
            open();
        },
        [open, setSelectedId]
    );

    return {
        selectedId,
        isOpen,
        isCreate,
        close,
        openNew: open,
        openExisting
    };
}

const widthMap = {
    sm: 'max-w-screen-sm',
    md: 'max-w-screen-md',
    lg: 'max-w-screen-lg',
    xl: 'max-w-screen-xl',
    '2xl': 'max-w-screen-2xl'
};

export default function SlideOver(props) {
    const { width = 'sm', isOpen, onClose, children } = props;

    const maxWidthToken = isNaN(width) && widthMap[width];
    const maxWidthValue = typeof width === 'number' && `${width}px`;
    return (
        <ManageControls.Provider>
            <Transition.Root show={isOpen} as={React.Fragment}>
                <Dialog as="div" onClose={onClose} static={true}>
                    <Transition.Child as={React.Fragment} {...transitions.opacity100}>
                        <div className="fixed inset-0" />
                    </Transition.Child>

                    <Transition.Child
                        as={React.Fragment}
                        className="bg-neutral-900 bg-opacity-50 duration-100 transition-all"
                        {...transitions.translate300}
                    >
                        <div className="fixed inset-0 z-10 flex flex-col overflow-auto">
                            <Dialog.Panel
                                style={{ maxWidth: maxWidthValue }}
                                className={`${maxWidthToken} min-h-auto ml-auto flex w-full flex-1 flex-col border-l border-neutral-200 bg-white shadow-lg`}
                                static={true}
                            >
                                <ManageControls className="sticky top-0 z-10 mb-1 bg-white/40 px-3 py-4 backdrop-blur-sm lg:px-5">
                                    <button
                                        type="button"
                                        className="mr-6 translate-y-[1px] text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">{/* {_t.bottone.apriBarraLaterale()} */}</span>
                                        <XMarkIcon className="h-6 w-6 text-neutral-700" aria-hidden="true" />
                                    </button>
                                    <div className="absolute left-0 -bottom-[1px] h-[1px] w-full bg-white/60 backdrop-blur-sm" />
                                </ManageControls>
                                <div className="relative flex flex-grow flex-col gap-3 pb-3 md:gap-5 md:px-3 lg:px-5 lg:pb-5">
                                    {children}
                                </div>
                            </Dialog.Panel>
                        </div>
                    </Transition.Child>
                </Dialog>
            </Transition.Root>
        </ManageControls.Provider>
    );
}
